// @flow
import {Store} from 'client/framework';
import appStore from 'client/AppStore';
import {mandarkEndpoint} from '@albert-io/json-api-framework/request/builder/legacy';
import {
  getAssignmentStudentStatistics,
  getStudentLevelGuessInsights
} from 'framework/MandarkAPI/Assignments/Assignments';

import type {AssignmentModelV2} from 'resources/Assignment/V2/Assignment.model';

function isEmptyOrSpaces(str) {
  return str === null || str.match(/^ *$/) !== null;
}

function getDisplayName(student) {
  let displayName = `${student.getFirstName()} ${student.getLastName()}`;
  if (isEmptyOrSpaces(displayName)) {
    displayName = student.getEmail();
  }
  return displayName;
}

/*
  XXX:

  This store as well as the other assignment drilldown stores are a heavy mix of things that could be
  getResource calls as well as several things that are stuck in the old assignments.js mode of GET-ing.


  This file has a getResource mixed with two interface style stats endpoints. This is a direct translation
  of what was previously nested into its own route accessible component.

  The assignment lookup includes students for the time being even though we could possibly change it to be a single
  student lookup because refactoring this fully rather than just translating it would be too much work for the time being
    - see dumb stuff like `getStudentName`

    We might be able to get most of this information thru META attributes, but at the time being that is not the case.
    so we are still leaning heavily on /stats  -- as we are in the rest of the drilldown, which I have not touched much.
*/
export class SingleStudentAssignmentStore extends Store {
  _getStudentLevelGuessInsights(): Object {
    const args = {
      classroomId: appStore.routerProps.params.classId,
      assignmentId: appStore.routerProps.params.assignmentId,
      studentId: appStore.routerProps.params.studentId
    };
    return getStudentLevelGuessInsights(args);
  }

  getStudentLevelGuessInsights(): Map<string, *> {
    return this._getStudentLevelGuessInsights().getData().get('data');
  }

  hasGuessInsights(): boolean {
    return this._getStudentLevelGuessInsights().hasReceivedData();
  }

  _makeClassroomScopedAssignmentQuery(): Object {
    return mandarkEndpoint(['assignments_v2', appStore.routerProps.params.assignmentId])
      .include('question_sets_v1.questions_v3,students_v1,classrooms_v1')
      .filter({
        included: {
          students_v1: {
            classrooms_v1: {
              id: appStore.routerProps.params.classId
            }
          }
        }
      })
      .customQuery({
        with_meta: 'student_v1,assignment_v2'
      });
  }

  getAssignment(): AssignmentModelV2 {
    return this._makeClassroomScopedAssignmentQuery().getResource();
  }

  hasAssignment(): boolean {
    return this._makeClassroomScopedAssignmentQuery().isResourcePopulated();
  }

  _getStudentStats(): Object {
    const args = {
      classroomId: appStore.routerProps.params.classId,
      assignmentId: appStore.routerProps.params.assignmentId
    };
    return getAssignmentStudentStatistics(args);
  }

  _getStudentStatsInterface(): Object {
    return this._getStudentStats().interface;
  }

  hasStudentStats(): boolean {
    return this._getStudentStats().hasReceivedData();
  }

  getStudent(): any {
    const {studentId} = appStore.routerProps.params;
    const students = this.getAssignment().getStudents();

    return students.find((student) => {
      return student.get('id') === studentId;
    });
  }

  /*
    XXX: I suspect we'll eventually do a lookup in students_v1 by id, but for now we're keeping the old version
    of this and grabbing the student from assignment's includes.
  */
  getStudentName(): string {
    const activeStudent = this.getStudent();

    if (!activeStudent) {
      return '';
    }

    return getDisplayName(activeStudent);
  }

  getQuestionsAnswered(): number {
    return this._getStudentStatsInterface().getQuestionsAnswered(
      appStore.routerProps.params.studentId
    );
  }

  getTotalQuestions(): number {
    return this.getAssignment().getQuestionCount();
  }
}

export default new SingleStudentAssignmentStore('SingleStudentAssignmentStore');
